import React, { useCallback, useEffect, useMemo } from 'react';
import { RouteComponentProps } from 'react-router';
import { isNull } from 'lodash/fp';
import { Options } from 'react-select';

import { useAppDispatch, useAppSelector } from '../../../hooks/react-redux';
import { useFetchStarted } from '../../../hooks/useFetchStarted';
import { useWindowResize } from '../../../hooks/useWindowResize';
import { fetchAllDropdownListsStarted } from '../dropdown-lists/store';
import { addOpinionSubCounterpartyType, fetchOpinionSubCounterpartyTypesSuccessful, getIsEditingSelectedSubCounterparty, getSelectedSubCounterpartyJurisdiction, getSelectedParentCounterparty, revertOpinionSubCounterparty, selectSubCounterpartyTypesJurisdiction, selectSubCounterpartyTypesParent, updateOpinionSubCounterparty, updateMultiParentCounterparties, setScopeFilters, getSubCounterpartyScopeFilters } from './store';
import { SubCounterpartyInputSection } from './SubCounterpartyInputSection';
import { SubCounterpartyJurisdictionSection } from './SubCounterpartyJurisdictionSection';
import styles from './SubCounterpartyTypes.module.scss';
import { getInternetGatewayOpen, getUserHasFeaturePermissionNoAdmin } from '../../auth/login/store';
import { PlusButton } from '../../shared/button/PlusButton';
import { FeaturePermission } from '../users/store';
import { DropdownOption } from '../../datasets/fields';
import { DropdownSummary } from '../../shared/dropdown/DropdownSummary';
import { scopeFormatter } from '../../shared/table/arkTableFormatters';
import { OpinionScope } from '../opinions/store';

const MODAL_WIDTH = 500;

export const SubCounterpartyTypes: React.FC<RouteComponentProps> = () => {
    const dispatch = useAppDispatch();
    useFetchStarted([fetchAllDropdownListsStarted()]);

    const [screenWidth, screenHeight] = useWindowResize();
    const parentOptionsHeight = useMemo(() => screenHeight - 305, [screenHeight]);

    const jurisdiction = useAppSelector(getSelectedSubCounterpartyJurisdiction);
    const parentCounterparty = useAppSelector(getSelectedParentCounterparty);
    const userHasIndustryStandardPermission = useAppSelector(getUserHasFeaturePermissionNoAdmin([FeaturePermission.MANAGE_INDUSTRY_STANDARD_OPINIONS]));
    const isEditing = useAppSelector(getIsEditingSelectedSubCounterparty);
    const selectedScopeFilters = useAppSelector(getSubCounterpartyScopeFilters);
    const internetGatewayOpen = useAppSelector(getInternetGatewayOpen);

    const scopeFilters = useMemo(() => selectedScopeFilters.length === 0 ? Object.values(OpinionScope) : selectedScopeFilters, [selectedScopeFilters]);

    const scopeOptions = useMemo(() => Object.values(OpinionScope).map(scope => ({ value: scope, label: scopeFormatter(scope) })), []);
    const scopeValue = useMemo(() => scopeOptions.filter(({ value }) => scopeFilters.includes(value)), [scopeOptions, scopeFilters]);

    const initialOffset = useMemo(() => (screenWidth * 0.96) - MODAL_WIDTH, [screenWidth]);

    const addNewSubCounterpartyType = useCallback(() => {
        dispatch(addOpinionSubCounterpartyType(userHasIndustryStandardPermission ? 1 : 0));
        dispatch(updateOpinionSubCounterparty('jurisdiction', jurisdiction));
        if (!isNull(parentCounterparty)) {
            dispatch(updateMultiParentCounterparties([parentCounterparty]));
        }
        if (selectedScopeFilters.length > 0) {
            dispatch(updateOpinionSubCounterparty('scopes', selectedScopeFilters));
        }
    }, [dispatch, userHasIndustryStandardPermission, jurisdiction, parentCounterparty, selectedScopeFilters]);

    useEffect(() => {
        if (!jurisdiction || !parentCounterparty) {
            dispatch(fetchOpinionSubCounterpartyTypesSuccessful([]));
        }
    }, [dispatch, parentCounterparty, jurisdiction]);

    useEffect(() => () => {
        dispatch(selectSubCounterpartyTypesJurisdiction(null));
        dispatch(revertOpinionSubCounterparty());
        dispatch(selectSubCounterpartyTypesParent(null));
    }, [dispatch]);

    const updateScopes = useCallback((option: DropdownOption | Options<DropdownOption> | null) => {
        let dropdownValue: OpinionScope[] = [];
        if (!isNull(option)) {
            dropdownValue = ((option as Options<DropdownOption>).map(({ value }) => value) as OpinionScope[]);
        }
        dispatch(setScopeFilters(dropdownValue));
    }, [dispatch]);

    return (
        <div className={styles.subCounterpartyTypesWrapper}>
            <div className={styles.subCounterpartyTypesHeader}>
                <div className={styles.subCounterpartyTypesTitleWrapper}>
                    <div className={styles.subCounterpartyTypesHeaderTitle} data-testid='sub-counterparties-title'>Sub Counterparty Types</div>
                    {!isNull(jurisdiction) && <PlusButton onClick={addNewSubCounterpartyType} fontSize={25} disabled={isEditing || internetGatewayOpen} />}
                </div>
                <div className={styles.scopeFilterWrapper}>
                    <div className={styles.scopeFilterLabel}>Scope</div>
                    <DropdownSummary
                        onChange={updateScopes}
                        value={scopeValue}
                        options={scopeOptions}
                        menuPortalTarget={document.body}
                        isMulti
                    />
                </div>
            </div>
            <div className={styles.subCounterpartyTypesMainWrapper}>
                <SubCounterpartyJurisdictionSection parentOptionsHeight={parentOptionsHeight} jurisdiction={jurisdiction} parentCounterparty={parentCounterparty} />
                <SubCounterpartyInputSection parentOptionsHeight={parentOptionsHeight} jurisdiction={jurisdiction} parentCounterparty={parentCounterparty} initialOffset={initialOffset} />
            </div>
        </div>
    );
};
